import type { Mark } from '@/types/domain';
import { isHigherBetter, normalizePruebaName, parseMarkToNum } from '@/lib/marks';
import {
  categoriaPorNacimiento,
  normalizarCategoriaMarca,
  type CategoriaAtleta,
} from '@/config/categorias';
import { stripDiacritics } from '@/lib/normalize';

// ─────────────────────────────────────────────────────────────────────────────
// Tipos
// ─────────────────────────────────────────────────────────────────────────────

export type SexoMinima = 'H' | 'M';
export type PistaMinima = 'AL' | 'PC';
export type CategoriaMinima = Exclude<CategoriaAtleta, 'Desconocida'>;

export interface Minima {
  prueba: string;
  sexo: SexoMinima;
  categoria: CategoriaMinima;
  pista: PistaMinima;
  year: number;
  minima: string;
  minimaRepesca: string;
  admitidos: number;
  /** Fecha límite ISO (YYYY-MM-DD). Inclusive: las marcas válidas son las
   *  hechas entre 1-nov-(año-1) y esta fecha (suele ser ~2 semanas antes
   *  del campeonato). */
  fechaLimite: string;
}

export type EstadoClasif = 'minima' | 'repesca' | 'ninguna';

export interface RankedAthlete {
  atleta: string;
  marca: string;
  marcaNum: number;
  fecha: string;
  lugar: string;
  viento: string;
  categoriaCompeticion: string;
  estado: EstadoClasif;
  /** Posición 1..N entre los que aparecen en el listado (mínima + repesca). */
  rank: number;
  /** True si entra dentro del corte de admitidos (rank <= admitidos). */
  admitido: boolean;
  /** Fecha de nacimiento del atleta (ISO YYYY-MM-DD) si está disponible.
   *  Se usa en la UI para distinguir homónimos: dos atletas con el mismo
   *  nombre pero distinta fechaNac no son la misma persona. */
  fechaNac?: string;
  /** Club registrado en la marca (Mark.club). Permite a la UI saber si una
   *  marca pertenece al club seleccionado sin cruzar índices. */
  club?: string;
  /** País del atleta (ISO 3 letras estilo RFEA). Distinto de `ESP` indica
   *  atleta extranjero, que la UI resalta para advertir que puede no ser
   *  elegible al Cto. España. */
  pais?: string;
  /** Sólo para rankings de relevos: equipo identificado como "Selección"
   *  (nacional o autonómica). Estos equipos no optan al Cto. España (es
   *  competición de clubs), por lo que la UI los marca en rojo y extiende
   *  el listado para mostrar los clubs reales que sí optan. */
  seleccion?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Parser CSV
// ─────────────────────────────────────────────────────────────────────────────

/** Parser CSV permisivo (RFC 4180 + soporte `;`). Detecta separador en la 1ª línea. */
export function parseCsvText(text: string): string[][] {
  const stripped = text.replace(/^﻿/, '');
  // Detectar separador por la primera línea no vacía.
  const firstLine = stripped.split(/\r?\n/, 1)[0] ?? '';
  const sep = (firstLine.match(/;/g)?.length ?? 0) > (firstLine.match(/,/g)?.length ?? 0)
    ? ';'
    : ',';

  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < stripped.length; i++) {
    const c = stripped[i];
    if (inQuotes) {
      if (c === '"') {
        if (stripped[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === sep) {
      row.push(field);
      field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && stripped[i + 1] === '\n') i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += c;
    }
  }
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

function normHeader(h: string): string {
  return stripDiacritics(h)
    .toLowerCase()
    .replace(/[\s_\-./]/g, '');
}

function normCategoria(raw: string): CategoriaMinima | null {
  const c = stripDiacritics(raw).toLowerCase().replace(/\s+/g, '');
  if (!c) return null;
  if (c.includes('master') || c.includes('veteran')) return 'Master';
  if (c.includes('absolut') || c.startsWith('senior') || c.includes('promesa-abs')) {
    return 'Absoluta';
  }
  const m = c.match(/sub(\d+)/);
  if (m) {
    const n = m[1];
    const allowed: Record<string, CategoriaMinima> = {
      '8': 'Sub 8',
      '10': 'Sub 10',
      '12': 'Sub 12',
      '14': 'Sub 14',
      '16': 'Sub 16',
      '18': 'Sub 18',
      '20': 'Sub 20',
      '23': 'Sub 23',
    };
    return allowed[n] ?? null;
  }
  return null;
}

function normSexo(raw: string): SexoMinima | null {
  // Convención del CSV: H = Hombres, M = Mujeres.
  const s = stripDiacritics(raw).toLowerCase().trim();
  if (!s) return null;
  if (s === 'h' || s.startsWith('hombre') || s.startsWith('masc') || s === 'male') return 'H';
  if (s === 'm' || s.startsWith('mujer') || s === 'f' || s.startsWith('fem')) return 'M';
  return null;
}

function normPista(raw: string): PistaMinima | null {
  const p = stripDiacritics(raw).toUpperCase().trim();
  if (p === 'AL' || p.startsWith('AIRE')) return 'AL';
  if (p === 'PC' || p.startsWith('PISTA') || p === 'INDOOR') return 'PC';
  return null;
}

/** Convierte el CSV de mínimas en `Minima[]`. Lanza si faltan columnas obligatorias. */
export function parseMinimasCsv(text: string): Minima[] {
  const rows = parseCsvText(text);
  if (rows.length < 2) throw new Error('CSV vacío o sin filas de datos.');

  const header = rows[0].map(normHeader);
  const col = (name: string): number => header.indexOf(name);

  const idx = {
    prueba: col('prueba'),
    sexo: col('sexo'),
    categoria: col('categoria'),
    pista: col('pista'),
    year: col('ano') >= 0 ? col('ano') : col('year'),
    minima: col('minima'),
    minimaRepesca: col('minimarepesca'),
    admitidos: col('admitidos'),
    fechaLimite: col('fechalimite'),
  };

  const missing = Object.entries(idx).filter(([, v]) => v < 0).map(([k]) => k);
  if (missing.length > 0) {
    throw new Error(`Faltan columnas en el CSV: ${missing.join(', ')}`);
  }

  const out: Minima[] = [];
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    const pruebaRaw = (row[idx.prueba] ?? '').trim();
    if (!pruebaRaw) continue;
    const sexo = normSexo(row[idx.sexo] ?? '');
    const categoria = normCategoria(row[idx.categoria] ?? '');
    const pista = normPista(row[idx.pista] ?? '');
    const year = parseInt((row[idx.year] ?? '').trim(), 10);
    const minima = (row[idx.minima] ?? '').trim();
    const minimaRepesca = (row[idx.minimaRepesca] ?? '').trim();
    const admitidos = parseInt((row[idx.admitidos] ?? '').trim(), 10);
    const fechaLimite = normalizarFechaISO((row[idx.fechaLimite] ?? '').trim());

    if (!sexo || !categoria || !pista || !Number.isFinite(year) || !fechaLimite) continue;
    const pruebaNorm = normalizePruebaName(pruebaRaw);
    out.push({
      prueba: pruebaNorm,
      sexo,
      categoria,
      pista,
      year,
      minima: normalizeMarcaCombinada(pruebaNorm, minima),
      minimaRepesca: normalizeMarcaCombinada(pruebaNorm, minimaRepesca),
      admitidos: Number.isFinite(admitidos) ? admitidos : 0,
      fechaLimite,
    });
  }
  return out;
}

/** Acepta "YYYY-MM-DD" o "DD/MM/YYYY"; devuelve ISO o '' si no parsea. */
function normalizarFechaISO(raw: string): string {
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const m = raw.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
  if (m) {
    const [, d, mo, y] = m;
    return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return '';
}

// ─────────────────────────────────────────────────────────────────────────────
// Emparejamiento prueba CSV ↔ prueba RFEA
// ─────────────────────────────────────────────────────────────────────────────

/** Clave canónica para emparejar pruebas. Empareja variantes habituales:
 *  "100 m" / "100m" / "100 m.l." → "100m"
 *  "60 m vallas" / "60mv" / "60 m.v." → "60mv"
 *  "3000 m obstáculos" / "3000m obst" → "3000mobst"
 *  "Salto de Altura" / "Altura" → "altura"
 *  "Lanzamiento de Peso" / "Peso" → "peso"
 *  "Triple Salto" / "Salto Triple" / "Triple" → "triple"
 *  "Decatlón" / "Decathlon" → "decatlon"
 */
export function pruebaKey(p: string): string {
  if (!p) return '';
  let s = stripDiacritics(normalizePruebaName(p)).toLowerCase().trim();
  // 1) Eliminar paréntesis (altura/peso del artefacto): "100 m v. (0,762)" →
  //    "100 m v.", "Disco (1kg)" → "Disco". Lo hacemos en bucle para soportar
  //    varios paréntesis encadenados.
  let prev = '';
  while (prev !== s) { prev = s; s = s.replace(/\s*\([^)]*\)\s*/g, ' ').trim(); }
  // 1b) Eliminar sufijos de altura de valla/obstáculo: "2.000m Obst. 0,838m" →
  //     "2.000m Obst.", "100 m v. 0,762m" → "100 m v.". También unidades sueltas
  //     "5 kg", "7,260 kg", "0,914 m" colgantes.
  s = s.replace(/\s+\d+[,.]\d+\s*(m|kg|gr|g)\b\.?/g, '').trim();
  // 2) Quitar sufijos RFEA de género/pista/categoría al final: "60 m v. MASC AL",
  //    "Decatlón Sub18", "Heptatlón Sub16 fem", etc. Iteramos para encadenados.
  let prevTail = '';
  while (prevTail !== s) {
    prevTail = s;
    s = s.replace(/\s+(masc|fem|sub\s*\d+|absoluta|absoluto|senior|master|veterano)s?\s*$/g, '').trim();
    s = s.replace(/\s+(al|pc|aire\s+libre|pista\s+cubierta|outdoor|indoor)\s*$/g, '').trim();
  }
  // 3) Quitar conectores y prefijos.
  s = s
    .replace(/\bsalto\s+con\s+/g, '')
    .replace(/\bsalto\s+de\s+/g, '')
    .replace(/\bsalto\s+/g, '')
    .replace(/\blanzamiento\s+de\s+/g, '')
    .replace(/\blanzamiento\s+/g, '')
    .replace(/\blanz\.?\s+de\s+/g, '')
    .replace(/\blanz\.?\s+/g, '')
    .replace(/\bcarrera\s+de\s+/g, '')
    .replace(/^l\s+/g, '')
    .replace(/^s\s+/g, '');
  // 4) Marcha: "3.000m Marcha" / "3.000m marcha pista" → "marcha".
  //    La distancia es indicativa; igualamos por "marcha" para que CSV
  //    "5.000 m / 5 km marcha" empareje sin importar la distancia exacta.
  if (/\bmarcha\b/.test(s)) {
    const distMatch = s.match(/(\d+(?:\.\d+)?)/);
    const dist = distMatch ? distMatch[1].replace('.', '') : '';
    return `${dist}marcha`;
  }
  // 5) Normalizar puntos y miles tipo "3.000 m" → "3000 m".
  s = s.replace(/(\d)\.(\d{3})\b/g, '$1$2');
  s = s.replace(/\.+/g, '');
  // 6) Vallas: "60 m vallas" / "60 mv" / "60 mh" / "60 m v" / "60 m h" → "60mv"
  s = s.replace(/(\d+)\s*m\s*(vallas?|hurdles?|v|h)\b/g, '$1mv');
  // 7) Obstáculos: "3000 m obstáculos" / "3000m obst" / "3000mobst" → "3000mobst"
  s = s.replace(/(\d+)\s*m\s*(obstaculos?|obst)\b/g, '$1mobst');
  // 8) Eliminar espacios.
  s = s.replace(/\s+/g, '');
  // 9) Suprimir sufijos comunes de RFEA en pistas lisas: "ml" / "l" final tras dígito.
  s = s.replace(/(\d)ml$/i, '$1m');
  s = s.replace(/(\d)l$/i, '$1');
  // 10) Combinadas: variantes ortográficas comunes (con/sin "h" inglesa).
  s = s.replace(/^decath?lon$/, 'decatlon');
  s = s.replace(/^heptath?lon$/, 'heptatlon');
  s = s.replace(/^pentath?lon$/, 'pentatlon');
  // "Hexatlón" / "Exatlón" (sin h, variante ortográfica) → hexatlon.
  s = s.replace(/^h?exath?lon$/, 'hexatlon');
  s = s.replace(/^octath?lon$/, 'octatlon');
  s = s.replace(/^triath?lon$/, 'triatlon');
  s = s.replace(/^biath?lon$/, 'biatlon');
  s = s.replace(/^tetrath?lon$/, 'tetratlon');
  // 11) "triplesalto" / "saltotriple" → "triple" (cubierto por (3) cuando hay
  //     espacios, pero la entrada puede venir sin espacios).
  if (s === 'triplesalto' || s === 'saltotriple') s = 'triple';
  if (s === 'saltoaltura' || s === 'alturasalto') s = 'altura';
  if (s === 'saltolongitud' || s === 'longitudsalto') s = 'longitud';
  if (s === 'saltopertiga' || s === 'pertigasalto') s = 'pertiga';
  return s;
}

/** Devuelve las claves de prueba que pueden emparejarse con una mínima dada.
 *  Casos especiales según sexo:
 *    "110/100 m vallas" + H → ["110mv"]; + M → ["100mv"]
 *    "Decatlón/Heptatlón" + H → ["decatlon"]; + M → ["heptatlon"]
 *  Para cualquier otra prueba con "/" (p.ej. "5.000 m / 5 km marcha")
 *  devolvemos todas las claves de cada parte separada por la barra.
 */
export function pruebaKeysForMinima(minima: Pick<Minima, 'prueba' | 'sexo'>): string[] {
  const norm = stripDiacritics(minima.prueba).toLowerCase().trim();
  if (/\b110\s*\/\s*100\b/.test(norm) || /\b100\s*\/\s*110\b/.test(norm)) {
    return minima.sexo === 'H' ? ['110mv'] : ['100mv'];
  }
  if (/decat(h|l)on.*hepta(h|l)on|hepta(h|l)on.*decat(h|l)on/.test(norm)) {
    return minima.sexo === 'H' ? ['decatlon'] : ['heptatlon'];
  }
  // Sub16: "Octatlón / (H)exatlón" (a veces escrito "Exatlón" sin h).
  if (/octat(h|l)on.*h?exat(h|l)on|h?exat(h|l)on.*octat(h|l)on/.test(norm)) {
    return minima.sexo === 'H' ? ['octatlon'] : ['hexatlon'];
  }
  // Marcha: el CSV "5.000 m / 5 km marcha" usa "/" como alternativa de unidad,
  // no como dos pruebas distintas. pruebaKey ya da la clave canónica ("Nmarcha").
  if (/\bmarcha\b/.test(norm)) {
    return [pruebaKey(minima.prueba)];
  }
  if (minima.prueba.includes('/')) {
    const parts = minima.prueba.split('/').map((p) => p.trim()).filter(Boolean);
    const keys = new Set<string>();
    for (const p of parts) {
      const k = pruebaKey(p);
      if (k) keys.add(k);
    }
    return [...keys];
  }
  return [pruebaKey(minima.prueba)];
}

// ─────────────────────────────────────────────────────────────────────────────
// Viento válido
// ─────────────────────────────────────────────────────────────────────────────

/** Pruebas AL en las que el viento decide la validez (velocidad y horizontales). */
function pruebaSometidaAViento(prueba: string): boolean {
  const p = stripDiacritics(prueba).toLowerCase();
  // Velocidades hasta 200 m (lisas y vallas)
  if (/^(\d+)\s*(m|ml|m\.l\.|mh|mv|m\s*h|m\s*v|m\s*vallas)?$/.test(p)) {
    const n = parseInt(p, 10);
    if (Number.isFinite(n) && n <= 200) return true;
  }
  // Patrones más laxos: "60m", "100mv", "110mh", "200m"...
  if (/^(60|80|100|110|150|200)\s*m(v|h|vallas)?$/.test(p)) return true;
  // Saltos horizontales
  if (/longitud|triple/.test(p)) return true;
  return false;
}

/** True si la marca cumple la regla de viento para Ctos. España.
 *  Sólo aplica en AL para velocidad y horizontales. Si la marca no trae dato
 *  de viento, se acepta (el origen RFEA puede no parsearlo); cuando esté
 *  disponible, se filtra estrictamente por ≤ +2.0. */
export function isVientoValido(mark: Pick<Mark, 'prueba' | 'pista' | 'viento' | 'lugar'>): boolean {
  if (pistaReal(mark as Mark) !== 'AL') return true;
  if (!pruebaSometidaAViento(mark.prueba)) return true;
  const raw = (mark.viento ?? '').trim().replace(',', '.');
  if (!raw) return true; // sin dato → aceptar (mejor incluir que ocultar)
  const n = parseFloat(raw);
  if (!Number.isFinite(n)) return true;
  return n <= 2.0;
}

// ─────────────────────────────────────────────────────────────────────────────
// Clasificación
// ─────────────────────────────────────────────────────────────────────────────

/** Inicio de temporada Cto. España: 1-nov del año anterior, ISO YYYY-MM-DD. */
export function seasonStartISO(year: number): string {
  return `${year - 1}-11-01`;
}

/** Nivel numérico de una categoría (mayor = "más alto"). Absoluta sobre todas. */
export function nivelCat(cat: string): number {
  switch (cat) {
    case 'Sub 8': return 8;
    case 'Sub 10': return 10;
    case 'Sub 12': return 12;
    case 'Sub 14': return 14;
    case 'Sub 16': return 16;
    case 'Sub 18': return 18;
    case 'Sub 20': return 20;
    case 'Sub 23': return 23;
    case 'Master': return 35;
    case 'Absoluta': return 100;
    default: return 0;
  }
}

/** True si la atleta (catReal) es elegible para una mínima de targetCategoria.
 *  Absoluta: cualquier atleta Sub16 en adelante. Para pruebas con
 *  artefacto/altura distinta por categoría, además se exige que la marca se
 *  haya hecho con artefacto absoluto (ver `requiereCatCompeticionGteAtleta`).
 *  Sub*: la categoría real debe coincidir exactamente. */
export function esElegiblePorEdad(catReal: CategoriaAtleta, target: CategoriaMinima): boolean {
  if (target === 'Absoluta') {
    return nivelCat(catReal) >= 16;
  }
  return catReal === target;
}

/** Para algunas pruebas el artefacto/altura varía por categoría: una marca
 *  hecha en una categoría inferior se hizo con un artefacto más ligero (peso,
 *  disco, martillo, jabalina) o una valla más baja, por lo que no debería
 *  contar para una categoría superior. Las combinadas también lo requieren
 *  porque contienen pruebas componentes (jabalina, peso, vallas) sometidas
 *  al mismo problema. */
export function requiereCatCompeticionGteAtleta(prueba: string): boolean {
  const p = stripDiacritics(prueba).toLowerCase();
  if (/peso|disco|martillo|jabalina|\bbala\b/.test(p)) return true;
  if (/valla|mv$|mh$/.test(p)) return true;
  if (/obstaculo|obst\b/.test(p)) return true;
  if (/decat(h|l)on|heptat(h|l)on|pentat(h|l)on/.test(p)) return true;
  return false;
}

/** En pruebas de vallas, para target Absoluta, devuelve el nivel mínimo de
 *  categoría POR EDAD que debe tener el atleta para que su marca cuente.
 *  Las alturas de valla de Sub20 y menores difieren de la absoluta en al
 *  menos una distancia/sexo, y el check de `m.categoria` no basta (un Sub18
 *  inscrito en una prueba etiquetada como "Absoluta" en un meeting habrá
 *  saltado con su altura, no con la absoluta). Tabla RFEA:
 *
 *   - 110 m vallas H: válida Sub23 + Absoluta (nivel ≥ 23).
 *   - 400 m vallas H: válida Sub20, Sub23, Absoluta (nivel ≥ 20).
 *   - 100 m vallas M: válida Sub20, Sub23, Absoluta (nivel ≥ 20).
 *   - 400 m vallas M: válida Sub18 en adelante (nivel ≥ 18).
 *
 *  Devuelve null si la combinación no aplica (target ≠ Absoluta o la
 *  prueba no es una valla con restricción de altura por categoría).
 */
export function nivelEdadMinVallasAbsoluta(
  prueba: string,
  sexo: SexoMinima,
  target: CategoriaMinima,
): number | null {
  if (target !== 'Absoluta') return null;
  const key = pruebaKey(prueba);
  if (sexo === 'H') {
    if (key === '110mv') return 23;
    if (key === '400mv') return 20;
    return null;
  }
  if (key === '100mv') return 20;
  if (key === '400mv') return 18;
  return null;
}

/** Categoría mínima en la que una marca puede haber sido hecha y seguir
 *  siendo válida para la `target` (atendiendo a artefactos/alturas que
 *  cambian entre Sub*).
 *
 *  - Para targets Sub*: la marca debe ser de esa misma categoría (umbral=target).
 *  - Para target Absoluta:
 *      * Decatlón: artefacto absoluto desde Sub23 (jabalina, peso, disco
 *        son ya los de senior; las vallas siguen siendo del decatlón).
 *      * Heptatlón / Pentatlón: artefacto absoluto desde Sub20.
 *      * Resto (vallas/lanzamientos/obstáculos): exige Absoluta.
 */
export function umbralCatComp(prueba: string, target: CategoriaMinima): CategoriaMinima {
  if (target !== 'Absoluta') return target;
  const p = stripDiacritics(prueba).toLowerCase();
  if (/decat(h|l)on/.test(p)) return 'Sub 23';
  if (/heptat(h|l)on|pentat(h|l)on/.test(p)) return 'Sub 20';
  return 'Absoluta';
}

/** Normaliza marcas de combinadas: "4.200" (separador de miles) → "4200".
 *  Detectamos por nombre (no por pruebaKey) para que "Decatlón / Heptatlón"
 *  también caiga aquí. */
export function normalizeMarcaCombinada(prueba: string, marca: string): string {
  const trimmed = (marca ?? '').trim();
  if (!trimmed) return trimmed;
  const norm = stripDiacritics(prueba).toLowerCase();
  const isCombinada = /decat(h|l)on|heptat(h|l)on|pentat(h|l)on/.test(norm);
  if (!isCombinada) return trimmed;
  if (/^\d{1,2}\.\d{3}$/.test(trimmed)) return trimmed.replace('.', '');
  if (/^\d{1,2}\.\d{3},\d+$/.test(trimmed)) return trimmed.replace('.', '');
  return trimmed;
}

/** Pista real de la marca: si está marcada como AL pero el lugar trae el
 *  marcador indoor "(i)" (o similar), se trata como PC. RFEA suele meter
 *  marcas de pista cubierta dentro de los listados de aire libre con el
 *  sufijo "(i)" en el lugar. */
export function pistaReal(m: Pick<Mark, 'pista' | 'lugar'>): 'AL' | 'PC' | string {
  if (m.pista === 'AL') {
    const lugar = (m.lugar ?? '').trim();
    if (/\(\s*i\s*\)|indoor|pista\s+cubierta/i.test(lugar)) return 'PC';
  }
  return m.pista;
}

/** Para Cto. España, una marca aplica a una pista si:
 *  - su pista coincide exactamente con la del campeonato, o
 *  - es una marca AL+indoor (lugar "(i)") y se busca para PC, o
 *  - es una marca AL+indoor y se busca para AL (las marcas indoor son válidas
 *    también para clasificarse al AL, según el reglamento de la RFEA).
 *  Es decir: una marca AL+(i) cuenta tanto para AL como para PC.
 */
export function aplicaParaPista(
  m: Pick<Mark, 'pista' | 'lugar'>,
  pistaTarget: 'AL' | 'PC',
): boolean {
  if (m.pista === pistaTarget) return true;
  if (m.pista === 'AL' && pistaTarget === 'PC') {
    return pistaReal(m) === 'PC';
  }
  return false;
}

function compareMarks(prueba: string, a: number, b: number): number {
  return isHigherBetter(prueba) ? b - a : a - b;
}

interface BestMark {
  mark: Mark;
  num: number;
}

export interface ClassifyOptions {
  /** Si la marca no trae fechaNac, intenta inferirla desde aquí (atletaNorm → ISO). */
  fechaNacByAtleta?: Map<string, string>;
  /** Si la marca no trae género, intenta inferirlo desde aquí. */
  generoByAtleta?: Map<string, 'M' | 'F'>;
}

/** Calcula el listado de clasificación para una mínima dada. */
export function classifyForMinima(
  minima: Minima,
  marks: Mark[],
  opts: ClassifyOptions = {},
): RankedAthlete[] {
  // Las combinadas vienen con separador de miles "." en CSVs antiguos.
  // Normalizamos aquí también, por si el doc en Firestore se guardó antes
  // de añadir la normalización al parser.
  const minNum = parseMarkToNum(
    minima.prueba,
    normalizeMarcaCombinada(minima.prueba, minima.minima),
  );
  const repNum = parseMarkToNum(
    minima.prueba,
    normalizeMarcaCombinada(minima.prueba, minima.minimaRepesca),
  );
  if (minNum === null && repNum === null) return [];

  const startISO = seasonStartISO(minima.year);
  const endISO = minima.fechaLimite;
  const targetKeys = new Set(pruebaKeysForMinima(minima));
  const targetCategoria = minima.categoria;
  const targetSexo: 'M' | 'F' = minima.sexo === 'H' ? 'M' : 'F';

  // Mejor marca por atleta cumpliendo todos los criterios.
  const best = new Map<string, BestMark>();
  for (const m of marks) {
    if (!aplicaParaPista(m, minima.pista)) continue;
    if (!targetKeys.has(pruebaKey(m.prueba))) continue;
    if (m.fecha < startISO || m.fecha > endISO) continue;
    const atletaKey = m.atletaNorm || stripDiacritics(m.atleta).toLowerCase();
    const generoEf = m.genero ?? opts.generoByAtleta?.get(atletaKey);
    if (generoEf && generoEf !== targetSexo) continue;
    const fechaNacEf = m.fechaNac || opts.fechaNacByAtleta?.get(atletaKey);
    if (!fechaNacEf) continue; // elegibilidad estricta exige fechaNac
    const catReal = categoriaPorNacimiento(fechaNacEf, minima.year);
    if (catReal === 'Desconocida') continue;
    if (!esElegiblePorEdad(catReal, targetCategoria)) continue;

    // Vallas en target Absoluta: además de la elegibilidad por edad general,
    // la altura de valla varía por sexo/distancia y excluye a categorías
    // menores. Por edad real del atleta filtramos las que físicamente
    // habrían saltado vallas distintas a la absoluta (un Sub18 inscrito en
    // una prueba "absoluta" de meeting saltó con su propia altura, no la
    // del Cto. España).
    const nivelMinAtleta = nivelEdadMinVallasAbsoluta(
      m.prueba,
      minima.sexo,
      targetCategoria,
    );
    if (nivelMinAtleta !== null && nivelCat(catReal) < nivelMinAtleta) continue;

    // En pruebas con artefacto/altura distinta por categoría, la marca sólo
    // vale si se hizo en una categoría con artefacto compatible con la
    // target. El umbral depende de la prueba: combinadas (decatlón/
    // heptatlón) no usan artefactos absolutos hasta Sub23/Sub20 según
    // sexo; resto exige la propia target.
    if (requiereCatCompeticionGteAtleta(m.prueba)) {
      const catCompNorm = normalizarCategoriaMarca(m.categoria);
      const umbral = umbralCatComp(m.prueba, targetCategoria);
      if (catCompNorm && nivelCat(catCompNorm) < nivelCat(umbral)) continue;
    }
    if (!isVientoValido(m)) continue;

    const num = parseMarkToNum(
      m.prueba,
      normalizeMarcaCombinada(m.prueba, m.marca),
    );
    if (num === null) continue;

    const prev = best.get(atletaKey);
    if (!prev || compareMarks(minima.prueba, num, prev.num) < 0) {
      best.set(atletaKey, { mark: m, num });
    }
  }

  const ranked: RankedAthlete[] = [...best.values()]
    .map(({ mark, num }) => {
      let estado: EstadoClasif = 'ninguna';
      if (minNum !== null && !worseThan(minima.prueba, num, minNum)) estado = 'minima';
      else if (repNum !== null && !worseThan(minima.prueba, num, repNum)) estado = 'repesca';
      const atletaKey = mark.atletaNorm || stripDiacritics(mark.atleta).toLowerCase();
      const fechaNacEf = mark.fechaNac || opts.fechaNacByAtleta?.get(atletaKey);
      return {
        atleta: mark.atleta,
        marca: mark.marca,
        marcaNum: num,
        fecha: mark.fecha,
        lugar: mark.lugar,
        viento: mark.viento,
        categoriaCompeticion: mark.categoria,
        estado,
        rank: 0,
        admitido: false,
        ...(fechaNacEf ? { fechaNac: fechaNacEf } : {}),
        ...(mark.club ? { club: mark.club } : {}),
        ...(mark.pais ? { pais: mark.pais } : {}),
      };
    })
    .filter((r) => r.estado !== 'ninguna')
    .sort((a, b) => compareMarks(minima.prueba, a.marcaNum, b.marcaNum));

  ranked.forEach((r, i) => {
    r.rank = i + 1;
    r.admitido = minima.admitidos > 0 && r.rank <= minima.admitidos;
  });
  return ranked;
}

/** True si `a` es peor que `b` (en términos de la prueba). */
function worseThan(prueba: string, a: number, b: number): boolean {
  return isHigherBetter(prueba) ? a < b : a > b;
}
